import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import { formatScore, rankWorkout, type RawScore, type WodSegment } from '@timecap/core';

import { Login } from './components/Login.js';
import { ScoreSheet } from './components/ScoreSheet.js';
import { toCoreScore, toCoreWorkout } from './lib/adapters.js';
import {
  fetchHeats,
  fetchSegments,
  fetchLanes,
  isFiltered,
  myLanes,
  fetchMyEvents,
  fetchScores,
  fetchTimerContext,
  fetchWorkouts,
  clockOffsetMs,
  type EventSummary,
  type HeatSummary,
  type LaneRow,
  type ScoreRow,
  type WorkoutSummary,
} from './lib/api.js';
import { enqueue, listQueued, queueKey, type QueuedScore, type ScorePayload } from './lib/queue.js';
import { supabase } from './lib/supabase.js';
import { drainQueue, retryRejected } from './lib/sync.js';
import { Loader } from './components/Loader';
import { LogoMark } from './components/LogoMark';
import { HeatTimer } from './components/HeatTimer.js';
import { WodCard } from './components/WodCard.js';

export default function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setReady(true);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, next) => setSession(next));
    return () => sub.subscription.unsubscribe();
  }, []);

  if (!ready)
    return (
      <div className="boot">
        <Loader />
      </div>
    );
  if (!session) return <Login />;
  return <JudgeBoard session={session} />;
}

function JudgeBoard({ session }: { session: Session }) {
  const [events, setEvents] = useState<EventSummary[]>([]);
  const [eventId, setEventId] = useState<string>('');
  const [workouts, setWorkouts] = useState<WorkoutSummary[]>([]);
  const [workoutId, setWorkoutId] = useState<string>('');
  const [heats, setHeats] = useState<HeatSummary[]>([]);
  const [heatId, setHeatId] = useState<string>('');
  const [allLanes, setAllLanes] = useState<LaneRow[]>([]);
  const [cargandoCarriles, setCargandoCarriles] = useState(false);
  // Lo que el juez ve y puntua: sus carriles, o todos si no le
  // asignaron ninguno. La lista completa se guarda para poder decirle
  // cuantos hay en total.
  const lanes = useMemo(() => myLanes(allLanes), [allLanes]);
  const filtrado = useMemo(() => isFiltered(allLanes), [allLanes]);
  const [scores, setScores] = useState<ScoreRow[]>([]);
  const [queued, setQueued] = useState<QueuedScore[]>([]);
  const [online, setOnline] = useState(navigator.onLine);
  const [selectedLane, setSelectedLane] = useState<LaneRow | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [timer, setTimer] = useState({ enabled: false, canControl: false });
  // Los movimientos del WOD en curso. Sin ellos el juez teclea las reps.
  const [segments, setSegments] = useState<WodSegment[]>([]);
  const [offsetMs, setOffsetMs] = useState(0);
  // Segundos capturados al tocar "Terminó", por inscripción. Se guardan
  // aparte del score porque son un dato en vuelo: existen entre el toque
  // y la confirmación, y no antes ni después.
  const [captured, setCaptured] = useState<Map<string, number>>(new Map());

  const eventoActual = events.find((e) => e.id === eventId);
  const eventoCaducado = Boolean(
    eventoActual?.expires_at && new Date(eventoActual.expires_at) <= new Date(),
  );
  const workout = workouts.find((w) => w.id === workoutId);
  const heat = heats.find((h) => h.id === heatId);

  // El desfase del reloj se mide una vez: no cambia durante la jornada.
  useEffect(() => {
    clockOffsetMs().then(setOffsetMs);
  }, []);

  const refreshQueue = useCallback(async () => {
    setQueued(await listQueued());
  }, []);

  // --- conectividad -------------------------------------------------
  useEffect(() => {
    function goOnline() {
      setOnline(true);
      drainQueue().then(refreshQueue);
    }
    function goOffline() {
      setOnline(false);
    }
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, [refreshQueue]);

  // Reintento periodico: la red del box va y viene sin avisar al navegador.
  useEffect(() => {
    refreshQueue();
    const id = setInterval(() => {
      drainQueue().then(refreshQueue);
    }, 15_000);
    return () => clearInterval(id);
  }, [refreshQueue]);

  // --- carga de datos -----------------------------------------------
  useEffect(() => {
    fetchMyEvents()
      .then((list) => {
        setEvents(list);
        if (list.length && !eventId) setEventId(list[0].id);
      })
      .catch((e) => setLoadError(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!eventId) return;
    fetchTimerContext(eventId).then(setTimer);
    fetchWorkouts(eventId)
      .then((list) => {
        setWorkouts(list);
        setWorkoutId(list.find((w) => w.status === 'running')?.id ?? list[0]?.id ?? '');
      })
      .catch((e) => setLoadError(e.message));
  }, [eventId]);

  const reloadHeats = useCallback(() => {
    if (!workoutId) return;
    fetchHeats(workoutId)
      .then(setHeats)
      .catch((e) => setLoadError(e.message));
  }, [workoutId]);

  useEffect(() => {
    if (!workoutId) {
      setSegments([]);
      return;
    }
    fetchSegments(workoutId)
      .then(setSegments)
      // Un WOD sin movimientos definidos es lo normal hasta que el
      // organizador los ponga: no es un error que deba ver el juez.
      .catch(() => setSegments([]));
  }, [workoutId]);

  useEffect(() => {
    if (!workoutId) return;
    Promise.all([fetchHeats(workoutId), fetchScores(workoutId)])
      .then(([heatList, scoreList]) => {
        setHeats(heatList);
        setScores(scoreList);
        setHeatId(heatList.find((h) => h.status === 'running')?.id ?? heatList[0]?.id ?? '');
      })
      .catch((e) => setLoadError(e.message));
  }, [workoutId]);

  /**
   * El WOD y el ajuste del cronometro tambien los cambia el organizador.
   *
   * Cambian pocas veces --poner el time cap que faltaba, publicar el
   * WOD, encender el reloj-- asi que no hace falta consultarlos cada
   * cinco segundos: basta al volver a la tablet o al recuperar la red.
   *
   * NO se toca `workoutId`: el efecto de carga inicial elige WOD, y
   * repetir esa eleccion aqui sacaria al juez del WOD que esta puntuando
   * cada vez que mira la pantalla.
   */
  useEffect(() => {
    if (!eventId) return;

    const refrescar = () => {
      if (!navigator.onLine || document.hidden) return;
      fetchTimerContext(eventId).then(setTimer).catch(() => {});
      fetchWorkouts(eventId).then(setWorkouts).catch(() => {});
    };

    document.addEventListener('visibilitychange', refrescar);
    window.addEventListener('online', refrescar);
    return () => {
      document.removeEventListener('visibilitychange', refrescar);
      window.removeEventListener('online', refrescar);
    };
  }, [eventId]);

  /**
   * El estado del heat lo cambia otra persona.
   *
   * Lo arranca el organizador desde el panel, o el head judge desde su
   * tablet. Antes solo se recargaba cuando el propio juez pulsaba algo,
   * asi que quien no lo hubiera arrancado se quedaba con el cronometro
   * parado hasta recargar la pagina a mano.
   *
   * Solo se refresca la lista de heats, nunca cual esta seleccionado:
   * cambiarle el heat a un juez a media captura seria peor que el fallo
   * que esto arregla.
   */
  useEffect(() => {
    if (!workoutId) return;

    const refrescar = () => {
      if (!navigator.onLine || document.hidden) return;
      reloadHeats();
    };

    // 5 s como la proyeccion. El instante de arranque viene del servidor,
    // asi que el reloj aparece hasta 5 s tarde pero YA contando desde el
    // punto correcto: no se desincroniza, solo tarda en asomar.
    const id = setInterval(refrescar, 5000);

    // Coger la tablet de la mesa no deberia costar cinco segundos.
    const alVolver = () => { if (!document.hidden) reloadHeats(); };
    document.addEventListener('visibilitychange', alVolver);
    window.addEventListener('online', reloadHeats);

    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', alVolver);
      window.removeEventListener('online', reloadHeats);
    };
  }, [workoutId, reloadHeats]);

  useEffect(() => {
    if (!heatId) {
      setAllLanes([]);
      return;
    }
    setCargandoCarriles(true);
    fetchLanes(heatId)
      .then(setAllLanes)
      .catch((e) => setLoadError(e.message))
      .finally(() => setCargandoCarriles(false));
  }, [heatId]);

  /**
   * El atleta acaba de terminar.
   *
   * Lo que importa es el INSTANTE del toque: la ficha puede confirmarse
   * diez segundos después sin que el tiempo cambie. Si se abriera la
   * ficha primero y se leyera el reloj al guardar, cada score llevaría
   * los segundos que el juez tardó en escribir.
   */
  const capturar = useCallback(
    (lane: LaneRow) => {
      if (!heat?.started_at) return;
      // Centesimas, no segundos enteros: dos atletas del mismo heat que
      // terminan con dos segundos de diferencia daban la misma marca si
      // el redondeo caia al mismo lado.
      //
      // Las milesimas no se guardan a proposito: en el toque del juez hay
      // dos o tres decimas de reaccion humana, y escribirlas seria dar
      // por medido lo que no se ha medido.
      const seg = Math.round(
        (Date.now() + offsetMs - new Date(heat.started_at).getTime()) / 10,
      ) / 100;
      setCaptured((prev) => new Map(prev).set(lane.registration_id, Math.max(0, seg)));
      setSelectedLane(lane);
    },
    [heat?.started_at, offsetMs],
  );

  // --- guardado -----------------------------------------------------
  const handleSave = useCallback(
    async (payload: ScorePayload) => {
      await enqueue(payload);

      // Pintado optimista: el juez ve el score al instante, sincronice o no.
      setScores((prev) => {
        const next = prev.filter(
          (s) => !(s.workout_id === payload.workout_id && s.registration_id === payload.registration_id),
        );
        next.push({
          id: `local-${queueKey(payload.workout_id, payload.registration_id)}`,
          workout_id: payload.workout_id,
          registration_id: payload.registration_id,
          status: payload.status,
          time_seconds: payload.time_seconds,
          reps: payload.reps,
          load_kg: payload.load_kg,
          distance_m: payload.distance_m,
          capped: payload.capped,
          tiebreak_seconds: payload.tiebreak_seconds,
          notes: payload.notes,
          segments_done: payload.segments_done,
          partial_reps: payload.partial_reps,
        });
        return next;
      });

      setCaptured((prev) => {
        const next = new Map(prev);
        next.delete(payload.registration_id);
        return next;
      });
      setSelectedLane(null);
      await refreshQueue();
      drainQueue().then(refreshQueue);
    },
    [refreshQueue],
  );

  // --- derivados ----------------------------------------------------
  const scoreByRegistration = useMemo(() => {
    const map = new Map<string, ScoreRow>();
    for (const s of scores) if (s.workout_id === workoutId) map.set(s.registration_id, s);
    return map;
  }, [scores, workoutId]);

  const queuedKeys = useMemo(() => new Map(queued.map((q) => [q.key, q])), [queued]);

  /**
   * Quien sigue compitiendo y quien ya termino, entre MIS carriles.
   *
   * Se cuenta sobre `lanes` y no sobre `allLanes`: un juez con tres
   * carriles asignados de ocho tiene que ver "faltan 2 de 3", no "faltan
   * 7 de 8". El aviso de arriba ya le dice que solo ve una parte.
   *
   * Un score en la cola cuenta como terminado: el atleta acabo, lo que
   * falta es que suba la red. Si no, el juez veria "sigue compitiendo" a
   * alguien que ya se fue del suelo.
   */
  const { pendientes, terminados } = useMemo(() => {
    let hechos = 0;
    for (const lane of lanes) {
      const enCola = queuedKeys.has(queueKey(workoutId, lane.registration_id));
      if (scoreByRegistration.has(lane.registration_id) || enCola) hechos++;
    }
    return { pendientes: lanes.length - hechos, terminados: hechos };
  }, [lanes, scoreByRegistration, queuedKeys, workoutId]);

  /**
   * Posicion provisional DENTRO del heat, calculada en la tablet con el
   * mismo motor que usa Postgres. Sirve sin conexion y es lo que el juez
   * necesita saber en el momento: quien va ganando este heat.
   */
  const heatPlaces = useMemo(() => {
    if (!workout || lanes.length === 0) return new Map<string, number>();
    const coreScores = new Map<string, RawScore>();
    for (const lane of lanes) {
      const row = scoreByRegistration.get(lane.registration_id);
      if (row) coreScores.set(lane.registration_id, toCoreScore(row));
    }
    const results = rankWorkout({
      workout: toCoreWorkout(workout),
      registrationIds: lanes.map((l) => l.registration_id),
      scores: coreScores,
      scoringTable: null,
    });
    return new Map(results.map((r) => [r.registrationId, r.place]));
  }, [workout, lanes, scoreByRegistration]);

  const pendingCount = queued.filter((q) => !q.rejectedReason).length;
  const rejected = queued.filter((q) => q.rejectedReason);

  return (
    <div className="app">
      <header className="topbar">
        <h1 className="brand">
          <LogoMark size={24} />
          Time<span>Cap</span>
        </h1>
        <div className="topbar-right">
          <SyncBadge online={online} pending={pendingCount} />
          <button className="ghost" onClick={() => supabase.auth.signOut()}>
            Salir
          </button>
        </div>
      </header>

      <div className="selectors">
        <Select label="Evento" value={eventId} onChange={setEventId}>
          {events.map((e) => (
            <option key={e.id} value={e.id}>
              {e.name}
              {e.expires_at && new Date(e.expires_at) <= new Date() ? ' · acceso caducado' : ''}
            </option>
          ))}
        </Select>
        <Select label="WOD" value={workoutId} onChange={setWorkoutId}>
          {workouts.map((w) => (
            <option key={w.id} value={w.id}>
              {w.name}
              {w.status === 'locked' ? ' (cerrado)' : ''}
            </option>
          ))}
        </Select>
        {/* El estado va en el propio nombre.
            Todos los heats se podian elegir --incluidos los cerrados--
            pero el desplegable solo ponia el nombre, asi que el juez no
            sabia cual estaba corriendo ni cual ya habia terminado:
            parecia que a los de antes no se podia volver. */}
        <Select label="Heat" value={heatId} onChange={setHeatId}>
          {heats.map((h) => (
            <option key={h.id} value={h.id}>
              {h.name}
              {h.status === 'running'
                ? ' · en curso'
                : h.status === 'review'
                  ? ' · terminado'
                  : h.status === 'closed'
                    ? ' · cerrado'
                    : h.started_at
                      ? ' · arrancado'
                      : ''}
            </option>
          ))}
        </Select>
      </div>

      {/* El acceso caduca 3 dias despues de cerrar la competencia. Sin
          este aviso, el juez veia el evento en su lista, entraba, y cada
          cosa que tocaba fallaba sin decirle por que. */}
      {eventoCaducado && (
        <p className="banner">
          Tu acceso a esta competencia caducó. Ya no puedes puntuar: pídele al organizador que te
          vuelva a invitar.
        </p>
      )}

      {workout?.status === 'locked' && (
        <p className="banner">Este WOD está cerrado. Solo un head judge puede modificar scores.</p>
      )}

      {rejected.length > 0 && (
        <div className="banner danger">
          <strong>{rejected.length} score(s) rechazados por el servidor.</strong>
          <ul>
            {rejected.map((r) => (
              <li key={r.key}>
                {r.rejectedReason}{' '}
                <button className="link" onClick={() => retryRejected(r.key).then(refreshQueue)}>
                  Reintentar
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {loadError && !lanes.length && <p className="banner danger">{loadError}</p>}

      {workout && <WodCard workout={workout} />}

      {/* El cronómetro va antes que los carriles: durante el heat, lo
          que el juez mira es cuánto queda para el corte.

          Arrancar el heat NO depende de que haya time cap ni de que el
          reloj esté activado: es lo que pone el heat "en curso" para la
          proyección, y `start_heat` no se llama desde ningún otro sitio.
          Antes esta condición exigía las dos cosas, así que un WOD sin
          tope no podía empezar nunca. */}
      {heat && (timer.enabled || timer.canControl) ? (
        <HeatTimer
          startedAt={heat.started_at}
          capSeconds={workout?.time_cap_seconds ?? null}
          showClock={timer.enabled}
          offsetMs={offsetMs}
          heatId={heat.id}
          canControl={timer.canControl}
          onChange={reloadHeats}
        />
      ) : null}

      {/* Que sepa si está viendo una parte o el heat entero: si cree que
          ve todo y solo ve lo suyo, dará por puntuado un heat a medias. */}
      {filtrado && (
        <p className="banner">
          Tus carriles: <strong>{lanes.length}</strong> de {allLanes.length} en este heat.
        </p>
      )}

      {/* Cuantos siguen en el suelo.
          Durante el heat la pregunta del juez es siempre la misma: a
          quien me falta. La lista lo dice --las filas puntuadas se ven
          distintas-- pero contarlas de un vistazo con ocho carriles y el
          reloj corriendo no es tan facil como parece. */}
      {heat?.started_at && lanes.length > 0 && (
        <p className={`banner ${pendientes === 0 ? 'ok' : ''}`}>
          {pendientes === 0 ? (
            <>Heat completo: los {lanes.length} carriles puntuados.</>
          ) : (
            <>
              Compitiendo: <strong>{pendientes}</strong> de {lanes.length}
              {terminados > 0 && <> · {terminados} ya terminaron</>}
            </>
          )}
        </p>
      )}

      <main className="lanes">
        {/* Mientras cargan, un loader. Antes decia "no hay carriles
            asignados" durante la espera: un juez que lee eso al abrir la
            tablet se va a buscar al organizador para nada. */}
        {cargandoCarriles && lanes.length === 0 && <Loader label="Cargando carriles…" size={56} />}
        {!cargandoCarriles && lanes.length === 0 && (
          <p className="empty">No hay carriles asignados a este heat.</p>
        )}
        {lanes.map((lane) => {
          const score = scoreByRegistration.get(lane.registration_id);
          const pending = queuedKeys.get(queueKey(workoutId, lane.registration_id));
          const place = heatPlaces.get(lane.registration_id);

          // Con el heat corriendo, un WOD por tiempo y sin marca todavía,
          // tocar la fila CAPTURA el instante. Es el gesto real del juez:
          // mira al atleta, no a la tablet, y toca su fila al terminar.
          // Un botón aparte obligaría a apuntar, y se perderían segundos.
          //
          // Lo enciende el organizador con el mismo interruptor del
          // cronómetro. Sin él, tocar la fila abre la ficha vacía y el
          // juez teclea el tiempo de su cronómetro de mano, como siempre.
          // Se comprueba `timer.enabled` y no solo `started_at`: si el
          // organizador apaga el cronómetro a mitad de jornada, los heats
          // ya arrancados conservan su started_at y la captura seguiría
          // disparándose sin que nadie la haya pedido.
          const capturable =
            timer.enabled &&
            Boolean(heat?.started_at) &&
            workout?.score_type === 'time' &&
            !score;

          return (
            <button
              key={lane.id}
              className={`lane ${score ? 'done' : ''} ${capturable ? 'capturable' : ''}`}
              onClick={() => (capturable ? capturar(lane) : setSelectedLane(lane))}
            >
              <span className="lane-number">{lane.lane_number}</span>
              <span className="lane-info">
                <span className="lane-name">{lane.display_name}</span>
                <span className="lane-meta">
                  {capturable ? (
                    <span className="lane-hint">Toca al terminar</span>
                  ) : (
                    <>
                      {lane.bib_number != null && <>#{lane.bib_number}</>}
                      {lane.box_name && <> · {lane.box_name}</>}
                    </>
                  )}
                </span>
              </span>
              <span className="lane-score">
                {score && workout ? (
                  <>
                    <span className="value">
                      {formatScore(toCoreWorkout(workout), toCoreScore(score))}
                    </span>
                    {place != null && <span className="place">{place}.º del heat</span>}
                  </>
                ) : (
                  <span className="value muted">Sin marcar</span>
                )}
                {pending && (
                  <span className={`pill ${pending.rejectedReason ? 'danger' : ''}`}>
                    {pending.rejectedReason ? 'rechazado' : 'sin sincronizar'}
                  </span>
                )}
              </span>
            </button>
          );
        })}
      </main>

      {selectedLane && workout && (
        <ScoreSheet
          workout={workout}
          lane={selectedLane}
          existing={scoreByRegistration.get(selectedLane.registration_id)}
          eventId={eventId}
          judgeId={session.user.id}
          capturedSeconds={captured.get(selectedLane.registration_id) ?? null}
          segments={segments}
          heatStartedAt={heat?.started_at ?? null}
          offsetMs={offsetMs}
          onSave={handleSave}
          onClose={() => {
            setCaptured((prev) => {
              const next = new Map(prev);
              next.delete(selectedLane.registration_id);
              return next;
            });
            setSelectedLane(null);
          }}
        />
      )}
    </div>
  );
}

function SyncBadge({ online, pending }: { online: boolean; pending: number }) {
  if (!online) {
    return (
      <span className="sync offline">
        Sin conexión{pending > 0 && <> · {pending} en cola</>}
      </span>
    );
  }
  if (pending > 0) return <span className="sync syncing">Sincronizando {pending}…</span>;
  return <span className="sync ok">Al día</span>;
}

function Select({
  label,
  value,
  onChange,
  children,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  children: React.ReactNode;
}) {
  return (
    <label className="select">
      <span className="field-label">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        {children}
      </select>
    </label>
  );
}
